/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `hsa_gyroscope_data` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class HsaGyroscopeDataMesg : Mesg {
    public constructor() : super("HsaGyroscopeData", Profile.MesgNum.HSA_GYROSCOPE_DATA)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** timestamp_ms, in ms. Millisecond resolution of the timestamp */
    public var timestampMs: UShort?
        get() = getFieldValue(TIMESTAMP_MS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TIMESTAMP_MS_FIELD_NUM, value)

    /** sampling_interval, in 1/32768 s. Sampling Interval in 32 kHz timescale */
    public var samplingInterval: UShort?
        get() = getFieldValue(SAMPLING_INTERVAL_FIELD_NUM) as? UShort
        set(value) = setFieldValue(SAMPLING_INTERVAL_FIELD_NUM, value)

    /** Number of values stored in gyro_x. */
    public val numGyroX: Int
        get() = getField(GYRO_X_FIELD_NUM)?.numValues ?: 0

    /** gyro_x, in deg/s. X-Axis Measurement */
    public var gyroX: List<Double?>?
        get() {
            val field = getField(GYRO_X_FIELD_NUM) ?: return null
            return List(field.numValues) { getGyroX(it) }
        }
        set(value) {
            removeField(GYRO_X_FIELD_NUM)
            value?.forEachIndexed { index, element -> setGyroX(index, element) }
        }

    public fun getGyroX(index: Int): Double? =
        getFieldValue(GYRO_X_FIELD_NUM, index) as? Double

    public fun setGyroX(index: Int, value: Double?) {
        setFieldValue(GYRO_X_FIELD_NUM, value, index)
    }

    /** Number of values stored in gyro_y. */
    public val numGyroY: Int
        get() = getField(GYRO_Y_FIELD_NUM)?.numValues ?: 0

    /** gyro_y, in deg/s. Y-Axis Measurement */
    public var gyroY: List<Double?>?
        get() {
            val field = getField(GYRO_Y_FIELD_NUM) ?: return null
            return List(field.numValues) { getGyroY(it) }
        }
        set(value) {
            removeField(GYRO_Y_FIELD_NUM)
            value?.forEachIndexed { index, element -> setGyroY(index, element) }
        }

    public fun getGyroY(index: Int): Double? =
        getFieldValue(GYRO_Y_FIELD_NUM, index) as? Double

    public fun setGyroY(index: Int, value: Double?) {
        setFieldValue(GYRO_Y_FIELD_NUM, value, index)
    }

    /** Number of values stored in gyro_z. */
    public val numGyroZ: Int
        get() = getField(GYRO_Z_FIELD_NUM)?.numValues ?: 0

    /** gyro_z, in deg/s. Z-Axis Measurement */
    public var gyroZ: List<Double?>?
        get() {
            val field = getField(GYRO_Z_FIELD_NUM) ?: return null
            return List(field.numValues) { getGyroZ(it) }
        }
        set(value) {
            removeField(GYRO_Z_FIELD_NUM)
            value?.forEachIndexed { index, element -> setGyroZ(index, element) }
        }

    public fun getGyroZ(index: Int): Double? =
        getFieldValue(GYRO_Z_FIELD_NUM, index) as? Double

    public fun setGyroZ(index: Int, value: Double?) {
        setFieldValue(GYRO_Z_FIELD_NUM, value, index)
    }

    /** timestamp_32k, in 1/32768 s. 32 kHz timestamp */
    public var timestamp32k: UInt?
        get() = getFieldValue(TIMESTAMP_32K_FIELD_NUM) as? UInt
        set(value) = setFieldValue(TIMESTAMP_32K_FIELD_NUM, value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val TIMESTAMP_MS_FIELD_NUM: UByte = 0u
        public const val SAMPLING_INTERVAL_FIELD_NUM: UByte = 1u
        public const val GYRO_X_FIELD_NUM: UByte = 2u
        public const val GYRO_Y_FIELD_NUM: UByte = 3u
        public const val GYRO_Z_FIELD_NUM: UByte = 4u
        public const val TIMESTAMP_32K_FIELD_NUM: UByte = 5u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("TimestampMs", 0u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            fields[1] = Field("SamplingInterval", 1u, BaseType.UINT16, 1.0, 0.0, "1/32768 s", false)
            fields[2] = Field("GyroX", 2u, BaseType.SINT16, 28.57143, 0.0, "deg/s", false)
            fields[3] = Field("GyroY", 3u, BaseType.SINT16, 28.57143, 0.0, "deg/s", false)
            fields[4] = Field("GyroZ", 4u, BaseType.SINT16, 28.57143, 0.0, "deg/s", false)
            fields[5] = Field("Timestamp32k", 5u, BaseType.UINT32, 1.0, 0.0, "1/32768 s", false)
            return fields
        }
    }
}
